import type { MapPublic } from '@il-colpo/engine';

export interface Point {
  x: number;
  y: number;
}

export interface MapLayout {
  width: number;
  height: number;
  nodes: Record<string, Point>;
  nodeWidth: number;
  nodeHeight: number;
}

export const NODE_W = 112;
export const NODE_H = 56;
const COL = 132;
const ROW = 100;
const PAD = 16;

/**
 * Lays a blueprint out in floors: the roof on top, the basement at the bottom, rooms of one
 * floor side by side. Within a floor, rooms are ordered by where their neighbours on the
 * floors above are, so that most lines run short and straight. The maps carry no coordinates
 * of their own (milestone 4 may add hand-drawn ones).
 */
export function layoutMap(map: MapPublic): MapLayout {
  const floors = [...new Set(map.rooms.map((r) => r.floor))].sort((a, b) => b - a);
  const neighbours = new Map<string, string[]>();
  const link = (a: string, b: string): void => {
    neighbours.set(a, [...(neighbours.get(a) ?? []), b]);
    neighbours.set(b, [...(neighbours.get(b) ?? []), a]);
  };
  for (const e of map.edges) link(e.a, e.b);
  for (const [a, b] of map.ropeLinks) link(a, b);

  const order = new Map<string, number>();
  const rows: string[][] = [];
  for (const floor of floors) {
    const ids = map.rooms.filter((r) => r.floor === floor).map((r) => r.id);
    const key = (id: string): number => {
      const placed = (neighbours.get(id) ?? []).filter((n) => order.has(n));
      if (placed.length === 0) return Number.POSITIVE_INFINITY;
      return placed.reduce((sum, n) => sum + (order.get(n) ?? 0), 0) / placed.length;
    };
    // Stable sort: rooms with no placed neighbour keep the map's own order.
    const sorted = ids
      .map((id, i) => ({ id, i, k: key(id) }))
      .sort((a, b) => (a.k === b.k ? a.i - b.i : a.k - b.k))
      .map((x) => x.id);
    sorted.forEach((id, i) => order.set(id, i));
    rows.push(sorted);
  }

  const widest = Math.max(...rows.map((r) => r.length));
  const width = PAD * 2 + (widest - 1) * COL + NODE_W;
  const height = PAD * 2 + (rows.length - 1) * ROW + NODE_H;
  const nodes: Record<string, Point> = {};
  rows.forEach((row, ri) => {
    const rowWidth = (row.length - 1) * COL + NODE_W;
    const left = (width - rowWidth) / 2;
    row.forEach((id, ci) => {
      nodes[id] = { x: left + ci * COL + NODE_W / 2, y: PAD + ri * ROW + NODE_H / 2 };
    });
  });
  return { width, height, nodes, nodeWidth: NODE_W, nodeHeight: NODE_H };
}
